import { db } from '@/lib/firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  Timestamp,
  arrayUnion
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import type { ChecklistTemplate, Checklist } from '../types/checklist.types';
import { Finding } from '../../auditcentre';
import { cleanDateAndTimeObject } from '@shared/renderDate';

// Helper functions to get collection references
const getCustomerRoot = (tenantId: string) => `customers/${tenantId}`;

// Templates are stored in: customers/{tenantId}/templates
const getTemplatesCollection = (tenantId: string) => 
  collection(db, 'customers', tenantId, 'templates');

// Checklists are stored in: customers/{tenantId}/checklists
const getChecklistsCollection = (tenantId: string) => 
  collection(db, 'customers', tenantId, 'checklists');

// Helper function to safely convert Firestore timestamps
const convertTimestamps = (data: any) => {
  const result = { ...data };
  
  if (data.createdAt) {
    result.createdAt = data.createdAt.toDate ? data.createdAt.toDate() : data.createdAt;
  }
  
  if (data.updatedAt) {
    result.updatedAt = data.updatedAt.toDate ? data.updatedAt.toDate() : data.updatedAt;
  }
  
  if (data.completedAt) {
    result.completedAt = data.completedAt.toDate ? data.completedAt.toDate() : data.completedAt;
  } else {
    result.completedAt = null;
  }

  if (data.reviewDate) {
    result.reviewDate = cleanDateAndTimeObject(data.reviewDate) ?? null;
  } else if (data.nextReviewDate || data.lastReviewDate) {
    const legacy = data.nextReviewDate ?? data.lastReviewDate;
    result.reviewDate = cleanDateAndTimeObject(legacy) ?? null;
  } else {
    result.reviewDate = null;
  }

  if (data.repeat && typeof data.repeat === 'object') {
    const repeat = { ...data.repeat };
    const start = 'startDate' in repeat ? repeat.startDate : undefined;
    const end = 'endDate' in repeat ? repeat.endDate : undefined;
    result.repeat = {
      ...repeat,
      ...(start !== undefined ? { startDate: cleanDateAndTimeObject(start) ?? null } : {}),
      ...(end !== undefined ? { endDate: cleanDateAndTimeObject(end) ?? null } : {}),
    };

    if (result.repeat && result.repeat.startDate === undefined) {
      result.repeat.startDate = cleanDateAndTimeObject(result.reviewDate ?? null) ?? null;
    }
    if (result.repeat && result.repeat.endDate === undefined) {
      result.repeat.endDate = null;
    }
  }
  
  return result;
};

// Convert Firestore data to ChecklistTemplate
const templateFromFirestore = (data: any): ChecklistTemplate => {
  const base = convertTimestamps(data);
  return {
    ...base,
    isReusable: Boolean(data.isReusable),
    isAssuranceTask: Boolean(data.isAssuranceTask),
    createdBy: data.createdBy || '',
    tenantId: data.tenantId || ''
  };
};

// Convert Firestore data to Checklist
const checklistFromFirestore = (data: any): Checklist => {
  const base = convertTimestamps(data);
  const normalizedStandards = Array.isArray(data.standards) && data.standards.length
    ? data.standards
    : (data.standard ? [data.standard] : []);
  // Parse findings array if present, else default to []
  return {
    ...base,
    status: (data.status as 'draft' | 'in-progress' | 'completed') || 'draft',
    standard: normalizedStandards?.[0] !== undefined ? normalizedStandards[0].replaceAll('Custom', '') : '',
    standards: normalizedStandards?.filter(s => s !== 'Custom'),
    completionHistory: Array.isArray(data.completionHistory)
      ? data.completionHistory.map((c: any) => ({
          ...c,
          completedAt: c.completedAt?.toDate ? c.completedAt.toDate() : c.completedAt,
        }))
      : [],
    templateId: data.templateId || '',
    createdBy: data.createdBy || '',
    tenantId: data.tenantId || '',
    isAssuranceTask: Boolean(data.isAssuranceTask),
    isReusable: typeof data.isReusable === 'boolean' ? data.isReusable : undefined,
    findings: Array.isArray(data.findings) ? data.findings : [],
    repeat: base.repeat,
    reviewDate: base.reviewDate ?? null,
  };
};

// Convert our model to Firestore data
const toFirestore = (data: Record<string, any>) => {
  const { id, ...rest } = data;
  // Convert all undefined values to null (Firestore does not allow undefined)
  const cleaned: Record<string, any> = {};
  for (const [key, value] of Object.entries(rest)) {
    if (key === 'standards' && Array.isArray(value)) {
      cleaned[key] = value;
    } else
    if (key === 'completionHistory' && Array.isArray(value)) {
      cleaned[key] = value.map((c: any) => ({
        ...c,
        completedAt: c.completedAt instanceof Date ? c.completedAt : (c.completedAt?.toDate ? c.completedAt.toDate() : c.completedAt),
      }));
    } else if (key === 'repeat' && value && typeof value === 'object') {
      cleaned[key] = {
        ...value,
        startDate: value.startDate === undefined ? null : value.startDate,
        endDate: value.endDate === undefined ? null : value.endDate,
      };
    } else if (key === 'findings' && Array.isArray(value)) {
      cleaned[key] = value;
    } else {
      cleaned[key] = value === undefined ? null : value;
    }
  }
  // Explicitly include isAssuranceTask if present
  return {
    ...cleaned,
    ...(typeof data.isAssuranceTask !== 'undefined' ? { isAssuranceTask: data.isAssuranceTask } : {}),
    ...(typeof data.isReusable !== 'undefined' ? { isReusable: data.isReusable } : {}),
    ...(typeof data.standard === 'string' ? { standard: data.standard } : {}),
    updatedAt: serverTimestamp(),
    ...(!rest.createdAt && { createdAt: serverTimestamp() })
  };
}

export const checklistService = {
  // Create a new blank checklist (not from template)
  async createChecklist(checklist: Omit<Checklist, 'id'> & { id?: string }, tenantId: string): Promise<Checklist> {
    const now = new Date();
    const id = checklist.id || uuidv4();
    const checklistRef = doc(getChecklistsCollection(tenantId), id);
    const standards = Array.isArray(checklist.standards) && checklist.standards.length
      ? checklist.standards
      : (checklist.standard ? [checklist.standard] : []);
    const firestoreData = {
      ...checklist,
      standard: standards[0] ?? '',
      standards,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    await setDoc(checklistRef, toFirestore(firestoreData));
    return {
      ...checklist,
      id,
      standard: standards[0] ?? '',
      standards,
      createdAt: now,
      updatedAt: now,
    };
  },
  // Template CRUD operations
  async getTemplate(id: string, tenantId: string): Promise<ChecklistTemplate | null> {
    try {
      const templateDoc = await getDoc(doc(getTemplatesCollection(tenantId), id));
      if (templateDoc.exists()) {
        return templateFromFirestore({ id: templateDoc.id, docID: templateDoc.id, ...templateDoc.data() });
      }
      return null;
    } catch (error) {
      console.error('Error getting template:', error);
      throw error;
    }
  },

  async getTemplates(tenantId: string): Promise<ChecklistTemplate[]> {
    try {
      const templatesQuery = query(
        getTemplatesCollection(tenantId),
        orderBy('title')
      );
      
      const querySnapshot = await getDocs(templatesQuery);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...templateFromFirestore(doc.data()),
        isSystem: false
      }));
    } catch (error) {
      console.error('Error getting templates:', error);
      throw error;
    }
  },

  async saveTemplate(template: Partial<ChecklistTemplate>): Promise<ChecklistTemplate> {
    if (!template.tenantId) {
      throw new Error('Tenant ID is required to save a template');
    }
    
    const id = template.id || uuidv4();
    const templateRef = doc(getTemplatesCollection(template.tenantId), id);
    
    // Prepare template data with timestamps
    const now = new Date();
    const templateCreatedAt = template.createdAt || now;
    
    // Create the template data with proper types
    const templateStandards = Array.isArray(template.standards) && template.standards.length
      ? template.standards
      : (template.standard ? [template.standard] : []);

    const templateData: Omit<ChecklistTemplate, 'id'> = {
      title: template.title || '',
      description: template.description || '',
      standard: template.standard || templateStandards[0] || '',
      standards: templateStandards,
      items: template.items || [],
      isReusable: template.isReusable ?? true,
      tenantId: template.tenantId,
      createdBy: template.createdBy || '',
      createdAt: templateCreatedAt,
      updatedAt: now,
      badgeClass: template.badgeClass || '',
      templateId: template.templateId || '',
      isAssuranceTask: template.isAssuranceTask ?? false
    };
    
    // Prepare Firestore data with server timestamps
    // We need to exclude the timestamps from templateData and add them separately
    // to avoid type conflicts with Firestore FieldValue
    const { createdAt: _, updatedAt: __, ...restTemplateData } = templateData;
    
    // Create a new object with the correct types for Firestore
    const firestoreData: Record<string, any> = { ...restTemplateData };
    
    // Add the timestamps as Firestore server timestamps
    firestoreData.updatedAt = serverTimestamp();
    firestoreData.createdAt = serverTimestamp();
    
    await setDoc(templateRef, firestoreData);
    return { ...templateData, id };
  },

  async deleteTemplate(id: string, tenantId: string): Promise<void> {
    const templateRef = doc(getTemplatesCollection(tenantId), id);
    await deleteDoc(templateRef);
  },

  // Create a new checklist from template
  async createChecklistFromTemplate(
    templateId: string, 
    tenantId: string, 
    createdBy: string
  ): Promise<Checklist> {
    const template = await this.getTemplate(templateId, tenantId);
    if (!template) {
      throw new Error('Template not found');
    }

    const now = new Date();
    const templateStandards = Array.isArray(template.standards) && template.standards.length
      ? template.standards
      : (template.standard ? [template.standard] : []);
    const checklistData: any = {
      templateId,
      title: template.title,
      description: template.description,
      items: template.items,
      isReusable: false,
      status: 'draft',
      tenantId,
      createdBy,
      createdAt: now,
      updatedAt: now,
      completedAt: null,
      completedBy: null,
      signature: null,
      isAssuranceTask: template.isAssuranceTask || false,
      standards: templateStandards,
      standard: templateStandards[0] ?? '',
      repeat: template.repeat ? { ...template.repeat } : undefined,
      reviewDate: template.reviewDate ?? null,
    };
    
    const checklistRef = doc(getChecklistsCollection(tenantId));
    
    // Prepare Firestore data with server timestamps
    const firestoreData = {
      ...checklistData,
      standard: templateStandards[0] ?? '',
      standards: templateStandards,
      repeat: template.repeat ? { ...template.repeat } : undefined,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(checklistRef, toFirestore(firestoreData));
    
    // Return the local data with the new ID
    return {
      ...checklistData,
      id: checklistRef.id
    } as Checklist;
  },

  async getChecklist(id: string, tenantId: string): Promise<Checklist | null> {
    try {
      const docRef = doc(getChecklistsCollection(tenantId), id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return checklistFromFirestore({ id: docSnap.id, ...docSnap.data() });
      }
      
      return null;
    } catch (error) {
      console.error('Error getting checklist:', error);
      throw error;
    }
  },

  async getChecklists(tenantId: string, templateId?: string): Promise<Checklist[]> {
    try {
      const conditions = [];
      if (templateId) {
        conditions.push(where('templateId', '==', templateId));
      }

      const q = query(
        getChecklistsCollection(tenantId),
        ...conditions,
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => 
        checklistFromFirestore({ id: doc.id, ...doc.data() })
      );
    } catch (error) {
      console.error('Error getting checklists:', error);
      throw error;
    }
  },

  async updateChecklist(
    id: string, 
    updates: Partial<Checklist>,
    tenantId: string
  ): Promise<void> {
    try {
      // Create a copy of updates to avoid mutating the original
      const updateData: any = { ...updates };
      
      // Always update the updatedAt timestamp
      updateData.updatedAt = serverTimestamp();
      
      // Ensure tenantId is set
      if (!updateData.tenantId) {
        updateData.tenantId = tenantId;
      }
      
      const checklistRef = doc(getChecklistsCollection(tenantId), id);
      
      // Convert to Firestore format before updating
      await updateDoc(checklistRef, toFirestore(updateData));
    } catch (error) {
      console.error('Error updating checklist:', error);
      throw error;
    }
  },

  async deleteChecklist(id: string, tenantId: string): Promise<void> {
    try {
      const checklistRef = doc(getChecklistsCollection(tenantId), id);
      await deleteDoc(checklistRef);
    } catch (error) {
      console.error('Error deleting checklist:', error);
      throw error;
    }
  },

  async submitChecklist(id: string, userId: string, tenantId: string, signature?: string, values?: Record<string, {title: string, value: any}>, findings?: Finding[]): Promise<void> {
    try {
      const checklistRef = doc(getChecklistsCollection(tenantId), id);
      await updateDoc(checklistRef, {
        status: 'completed',
        updatedAt: serverTimestamp(),
        completionHistory: arrayUnion({
          completedAt: new Date(),
          completedBy: userId,
          ...(values ? { values } : {}),
          ...(signature ? { signature } : {}),
          findings: findings || values?.findings || []
        })
      });
    } catch (error) {
      console.error('Error submitting checklist:', error);
      throw error;
    }
  }
};
